import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, UserCog, GitMerge, CalendarClock,
  ShieldCheck, LogOut, CalendarDays, Stethoscope, Map as MapIcon, KeyRound
} from "lucide-react";

const adminSections = [
  {
    label: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/admin/clients", label: "Clients", icon: Users },
      { to: "/admin/therapists", label: "Therapists", icon: UserCog },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/admin/matching", label: "Matching", icon: GitMerge },
      { to: "/admin/schedule", label: "Schedule", icon: CalendarClock },
      { to: "/admin/map", label: "Map", icon: MapIcon },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/insurance", label: "Insurance", icon: ShieldCheck },
      { to: "/admin/users", label: "Users", icon: KeyRound },
    ],
  },
];

const flatByRole = {
  therapist: [
    { to: "/therapist", label: "My Schedule", icon: CalendarDays, end: true },
  ],
  client: [
    { to: "/client", label: "My Appointments", icon: Stethoscope, end: true },
  ],
};

function getInitials(name) {
  return name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const flatItems = flatByRole[user?.role] || [];

  return (
    <div className="min-h-screen bg-base flex" data-testid="app-layout">
      <aside className="w-64 bg-surface border-r border-soft flex flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-soft">
          <Link to="/" className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shadow-[0_2px_8px_rgba(39,79,56,0.25)]"
              style={{ backgroundColor: "#274f38" }}
            >
              <span className="text-white font-bold text-lg leading-none">O</span>
            </div>
            <div>
              <div className="font-semibold text-[17px] tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>Ohana</div>
              <div className="text-xs text-muted-ohana -mt-0.5">Scheduler</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {isAdmin ? (
            <div className="space-y-5">
              {adminSections.map((section) => (
                <div key={section.label}>
                  <div className="px-3 mb-1.5 text-[10px] font-semibold tracking-[0.12em] uppercase text-muted-ohana">
                    {section.label}
                  </div>
                  <div className="space-y-0.5">
                    {section.items.map((it) => (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        end={it.end}
                        data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 ${
                            isActive
                              ? "bg-[#E8F0EA] text-[#274f38] font-semibold border-l-[3px] border-[#274f38] pl-[calc(0.75rem-3px)]"
                              : "text-[#586960] hover:bg-[#F5F3EE]"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <it.icon size={18} strokeWidth={isActive ? 2.2 : 1.6} />
                            {it.label}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-0.5">
              {flatItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 ${
                      isActive
                        ? "bg-[#E8F0EA] text-[#274f38] font-semibold border-l-[3px] border-[#274f38] pl-[calc(0.75rem-3px)]"
                        : "text-[#586960] hover:bg-[#F5F3EE]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <it.icon size={18} strokeWidth={isActive ? 2.2 : 1.6} />
                      {it.label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-soft">
          <div className="flex items-center gap-3 px-3 py-2.5 mb-1">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: "#274f38" }}
            >
              <span className="text-white text-xs font-semibold">{getInitials(user?.name)}</span>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#18231E] truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-muted-ohana capitalize">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-red-600 hover:bg-[#FDF4F4] transition-colors duration-150"
          >
            <LogOut size={18} strokeWidth={1.6} />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
